import { useState, useMemo } from 'react';
import { codeLabel } from '../../utils/codeLabel';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getAdminInterests,
} from '../../api/endpoints/admin';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type {
  AdminInterestSummaryResponse,
  AdminInterestDetailResponse,
  AdminInterestQueueParams,
} from '../../api/types';
import { INDIAN_STATES } from '../../config/indianStates';
import { DataTable } from '../../components/ui/DataTable';
import { Pagination } from '../../components/ui/Pagination';
import { StatusPill } from '../../components/ui/StatusPill';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { SearchableSelect, type SearchableOption } from '../../components/ui/SearchableSelect';
import { interestStatusColor } from '../../components/domain/statusColor';
import { InterestDetailModal } from './InterestDetailModal';
import { BidFormModal } from '../shipments/BidFormModal';
import styles from './InterestsView.module.css';
import { operationalQueryPolicy, safePagePlaceholder } from '../../api/queryPolicies';

type QueueTab = 'ALL' | 'NEW' | 'MINE' | 'DUE_TODAY' | 'OVERDUE' | 'CLOSED';

const QUEUE_TABS: { label: string; value: QueueTab }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'New', value: 'NEW' },
  { label: 'Mine', value: 'MINE' },
  { label: 'Due Today', value: 'DUE_TODAY' },
  { label: 'Overdue', value: 'OVERDUE' },
  { label: 'Closed', value: 'CLOSED' },
];

function isOverdue(nextFollowUpAt: string | null): boolean {
  if (!nextFollowUpAt) return false;
  return new Date(nextFollowUpAt).getTime() < Date.now();
}

function isDueToday(nextFollowUpAt: string | null): boolean {
  if (!nextFollowUpAt) return false;
  const followUpDate = new Date(nextFollowUpAt);
  const now = new Date();
  return (
    followUpDate.getFullYear() === now.getFullYear() &&
    followUpDate.getMonth() === now.getMonth() &&
    followUpDate.getDate() === now.getDate()
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function InterestsView() {
  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const stateRestore = (location.state as {
    activeTab?: QueueTab;
    page?: number;
    search?: string;
    originState?: string;
    destinationState?: string;
  } | null);

  const initialTab = (searchParams.get('tab') as QueueTab | null) ?? stateRestore?.activeTab ?? 'ALL';
  const initialPage = searchParams.get('page') ? Number(searchParams.get('page')) : (stateRestore?.page ?? 0);
  const initialSearch = searchParams.get('search') ?? stateRestore?.search ?? '';
  const initialOriginState = searchParams.get('originState') ?? stateRestore?.originState ?? '';
  const initialDestinationState = searchParams.get('destinationState') ?? stateRestore?.destinationState ?? '';

  const [activeTab, setActiveTab] = useState<QueueTab>(initialTab);
  const [page, setPage] = useState(!Number.isNaN(initialPage) ? initialPage : 0);
  const [search, setSearch] = useState(initialSearch);
  const debouncedSearch = useDebouncedValue(search, 300, () => setPage(0));
  const [originState, setOriginState] = useState(initialOriginState);
  const [destinationState, setDestinationState] = useState(initialDestinationState);

  const buildReturnUrl = () => {
    const params = new URLSearchParams();
    if (activeTab !== 'ALL') params.set('tab', activeTab);
    if (page > 0) params.set('page', String(page));
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (originState) params.set('originState', originState);
    if (destinationState) params.set('destinationState', destinationState);
    const qs = params.toString();
    return qs ? `${location.pathname}?${qs}` : location.pathname;
  };

  const navState = {
    from: buildReturnUrl(),
    fromLabel: 'Interests',
    restoreState: {
      activeTab,
      page,
      search: debouncedSearch,
      originState,
      destinationState,
    },
  };

  // Selected interest for detail modal or bid form
  const [selectedInterestId, setSelectedInterestId] = useState<number | null>(null);
  const [bidConvertInterest, setBidConvertInterest] = useState<AdminInterestSummaryResponse | AdminInterestDetailResponse | null>(null);

  const stateOptions: SearchableOption[] = useMemo(
    () =>
      INDIAN_STATES.map((s) => ({
        id: s.name,
        label: s.name,
        subtitle: s.capital,
      })),
    [],
  );

  const handleSearchChange = (v: string) => {
    setSearch(v);
  };

  const resetFilters = () => {
    setSearch('');
    setOriginState('');
    setDestinationState('');
    setPage(0);
  };

  const queryKey = [
    'admin',
    'interests',
    activeTab,
    page,
    debouncedSearch,
    originState,
    destinationState,
  ] as const;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => {
      const params: AdminInterestQueueParams = {
        page,
        size: 25,
        originState: originState || undefined,
        destinationState: destinationState || undefined,
        shipmentUuid: debouncedSearch ? debouncedSearch.trim() : undefined,
      };

      if (activeTab === 'NEW') {
        params.status = 'NEW';
      } else if (activeTab === 'MINE') {
        params.mine = true;
      } else if (activeTab === 'DUE_TODAY') {
        params.dueFilter = 'DUE_TODAY';
      } else if (activeTab === 'OVERDUE') {
        params.dueFilter = 'OVERDUE';
      } else if (activeTab === 'CLOSED') {
        params.status = 'CLOSED';
      }

      return getAdminInterests(params);
    },
    ...operationalQueryPolicy,
    placeholderData: safePagePlaceholder(queryKey, 3),
  });

  return (
    <div className={styles.page}>
      {/* Search & Tabs Toolbar */}
      <div className={styles.toolbar}>
        <input
          id="interests-search"
          className={styles.search}
          type="search"
          placeholder={t('interests.interestsView.searchPlaceholder')}
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />

        <div className={styles.filters}>
          {QUEUE_TABS.map((tab) => (
            <button
              key={tab.value}
              className={
                activeTab === tab.value
                  ? `${styles.chip} ${styles.chipActive}`
                  : styles.chip
              }
              onClick={() => {
                setActiveTab(tab.value);
                setPage(0);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <RefreshButton queryKeys={[['admin', 'interests']]} />
      </div>

      {/* Origin / Destination Filters */}
      <div className={styles.filterRow}>
        <div className={styles.stateSelectWrapper}>
          <SearchableSelect
            options={stateOptions}
            value={originState}
            onChange={(val) => {
              setOriginState(val);
              setPage(0);
            }}
            placeholder={t('interests.interestsView.allOriginStates')}
            allowEmpty
          />
        </div>

        <div className={styles.stateSelectWrapper}>
          <SearchableSelect
            options={stateOptions}
            value={destinationState}
            onChange={(val) => {
              setDestinationState(val);
              setPage(0);
            }}
            placeholder={t('interests.interestsView.allDestinationStates')}
            allowEmpty
          />
        </div>

        {(originState || destinationState || search) && (
          <button className={styles.resetBtn} onClick={resetFilters}>
            {t('interests.interestsView.resetFilters')}
          </button>
        )}
      </div>

      {/* DataTable */}
      <DataTable
        loading={isLoading}
        error={isError ? "We couldn’t load interests" : undefined}
        onRetry={() => refetch()}
        empty="No partner interests found for the selected queue/filters."
        rows={data?.items ?? []}
        keyFn={(r: AdminInterestSummaryResponse) => r.id}
        columns={[
          {
            key: 'route',
            header: 'Route / Load',
            render: (r: AdminInterestSummaryResponse) => (
              <div className={styles.routeCell}>
                <Link
                  to={`/shipments/${r.shipmentId}`}
                  state={navState}
                  className={`${styles.detailLink} ${styles.routeText}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {r.routeLabel ||
                    `${r.pickupCity || '—'}, ${r.pickupStateName || r.pickupState || '—'} → ${r.dropCity || '—'}, ${r.dropStateName || r.dropState || '—'}`}
                </Link>
                <span className={styles.uuidText}>{t('interests.interestsView.cycle', { cycle: r.postingCycle })}</span>
              </div>
            ),
          },
          {
            key: 'vendor',
            header: 'Partner',
            render: (r: AdminInterestSummaryResponse) => (
              <div className={styles.vendorCell}>
                <span className={styles.vendorName}>
                  <Link
                    to={`/users/${r.vendorId}`}
                    className={styles.detailLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {r.vendorName || `Partner #${r.vendorId}`}
                  </Link>
                </span>
                <span className={styles.vendorPhone}>
                  {r.vendorPhone || 'No phone'}
                </span>
              </div>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            render: (r: AdminInterestSummaryResponse) => {
              const colors = interestStatusColor(r.status);
              return (
                <StatusPill
                  label={codeLabel('interestStatus', r.status)}
                  bg={colors.bg}
                  text={colors.text}
                />
              );
            },
          },
          {
            key: 'owner',
            header: 'Owner',
            render: (r: AdminInterestSummaryResponse) => (
              <span style={{ fontSize: 12.5 }}>
                {r.ownerAdminName ? (
                  <span>
                    {t('interests.interestsView.orderOwner')}{' '}
                    <Link
                      to={`/shipments/${r.shipmentId}`}
                      state={navState}
                      className={styles.detailLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {r.ownerAdminName}
                    </Link>
                  </span>
                ) : (
                  <Link
                    to={`/shipments/${r.shipmentId}`}
                    state={navState}
                    className={styles.detailLink}
                    style={{ color: 'var(--muted)' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('interests.interestsView.unassigned')}
                  </Link>
                )}
              </span>
            ),
          },
          {
            key: 'followUp',
            header: 'Follow-up',
            render: (r: AdminInterestSummaryResponse) => {
              const overdue = isOverdue(r.nextFollowUpAt);
              const dueToday = isDueToday(r.nextFollowUpAt);
              return (
                <div className={styles.dueCell}>
                  <span>{fmtDate(r.nextFollowUpAt)}</span>
                  {overdue && (
                    <span className={styles.dueBadgeOverdue}>{t('interests.interestsView.overdue')}</span>
                  )}
                  {!overdue && dueToday && (
                    <span className={styles.dueBadgeToday}>{t('interests.interestsView.dueToday')}</span>
                  )}
                </div>
              );
            },
          },
          {
            key: 'vendorNote',
            header: 'Partner note',
            render: (r: AdminInterestSummaryResponse) => (
              <div className={styles.noteCell} title={r.vendorNote || ''}>
                {r.vendorNote || '—'}
              </div>
            ),
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (r: AdminInterestSummaryResponse) => (
              <div className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
                <button
                  className={styles.viewBtn}
                  onClick={() => setSelectedInterestId(r.id)}
                >
                  {t('interests.interestsView.manage')}
                </button>
              </div>
            ),
          },
        ]}
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          totalItems={data.totalItems}
          size={data.size}
          onPage={setPage}
        />
      )}

      {/* Detail Modal */}
      {selectedInterestId && (
        <InterestDetailModal
          interestId={selectedInterestId}
          onClose={() => setSelectedInterestId(null)}
          returnNavState={navState}
          onConvertToBid={(detail) => {
            setSelectedInterestId(null);
            setBidConvertInterest(detail);
          }}
        />
      )}

      {/* Bid Conversion Modal */}
      {bidConvertInterest && (
        <BidFormModal
          shipmentId={bidConvertInterest.shipmentId}
          initialBidderId={bidConvertInterest.vendorId}
          interest={bidConvertInterest}
          onClose={() => setBidConvertInterest(null)}
        />
      )}
    </div>
  );
}
